/* eslint-disable @typescript-eslint/no-explicit-any */


import React from 'react';
import { GenericTableColumn } from '@ui-components/Table/GenericTable';
import { GenericTag } from '@ui-components/Tag/GenericTag';
import GenericButton from '@ui-components/Button/GenericButton';
import { ReactComponent as IconConfig } from '../../../../shared/assets/Icons/IconSettings.svg';
import { TagsWithPopover } from 'kontrolEdge/components/Shared/TagsWithPopover/TagsWithPopover';

// Legacy interface - kept for backward compatibility during migration
export interface CloudPropagationNeighbor {
  bgpNeighborIp: string;
  peerAs: string;
  mode: 'Enterprise' | 'Telco';
  lastUpdate: string;
  bgpNeighborId?: number;
  reflectorInstanceId?: number; // Add reflector instance ID
}

// New interface for BGP Neighbor in expanded view
export interface BgpNeighborAssociation {
  neighborIp: string;
  peerAs: string | number;
  bgpNeighborId?: number;
}

// New interface for Cloud Propagation Rule (grouped by rule ID)
export interface CloudPropagationRuleRow {
  subId: string; // CP rule ID formatted as "CP-XXXXX" or "CP-<uuid-prefix>"
  name?: string; // Optional display name for the rule
  bgpNeighborsCount: number; // Number of associated BGP neighbors
  bgpNeighbors: BgpNeighborAssociation[]; // Array of associated BGP neighbors
  mode: 'Enterprise' | 'Telco';
  lastUpdate: string;
  propagationRuleId: number | string; // Rule ID (numeric or UUID string for get/delete)
  reflectorInstanceId?: number;
  // Store full rule data for edit operations
  ruleData?: CloudPropagationRule;
}

// Interface for the API response structure
export interface CloudPropagationApiResponse {
  message: string;
  instanceId: string;
  instanceName: string;
  total: number;
  data: CloudPropagationRule[];
}

// New API response format - direct array of propagation rules
export interface NewPropagationRuleApiItem {
  propagationRuleId: number;
  propagationRuleSubUuid: string; // e.g., "CP-00824"
  name?: string;
  type: "INBOUND" | "OUTBOUND" | "BOTH";
  mode: "ENTERPRISE" | "TELCO";
  lastUpdated: string; // Formatted date string
  associatedNeighbors: Array<{
    neighborAddress: string;
    peerAs: number;
    id?: number; // Optional BGP neighbor ID if available
  }>;
  inbounds?: any[];
  outbounds?: any[];
}

export interface CloudPropagationRule {
  id: number;
  bgpNeighborId: number;
  name?: string;
  propagationType: string;
  mode: string;
  createdAt: string;
  updatedAt: string;
  bgpNeighbor: {
    id: number;
    neighborAddress: string;
    peerGroupId: string;
    interfaceAddressId: number;
    peerAs: number;
    localAs: number;
    description: string;
    operatingMode: string;
    propagationMode: string;
    holdTime: number;
    keepAliveInterval: number;
    connectRetry: number;
    gracefulRestart: boolean;
    addPath: boolean;
    BFD: boolean;
    bfdInterval: number;
    bfdRequiredMinRxInterval?: number | null;
    bfdMultiplier: number;
    password: string | null;
    status: string | null;
    reflectorInstanceId: number;
    createdAt: string;
    updatedAt: string;
    reflectorInstance: {
      id: number;
      name: string;
    };
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  inbound: any | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  outbound: any | null;
}


export const cloudPropagationColumns: GenericTableColumn<CloudPropagationNeighbor>[] = [
  { header: 'BGP NEIGHBOR IP', accessor: 'bgpNeighborIp' },
  { header: 'PEER AS', accessor: 'peerAs' },
  {
    header: 'MODE',
    accessor: 'mode',
    renderCell: (row) => (
      <GenericTag
        label={row.mode}
        variant={row.mode === 'Enterprise' ? 'neutral' : 'warning'}
      />
    ),
  },
  { header: 'LAST UPDATED', accessor: 'lastUpdate' },
  {
    header: 'ACTIONS',
    accessor: undefined,
    renderCell: (row, rowIndex, onConfigure) => (
      <GenericButton
        isIconOnly
        icon={<IconConfig className="w-4 h-4 text-semantic-text-secondary" />}
        variant="secondary"
        shape="filled"
        size="sm"
        aria-label="Configure"
        onClick={() => onConfigure?.(rowIndex)}
      />
    ),
  },
];

/** Display value or '-' when undefined/null/empty */
function orDash(value: string | number | undefined | null): React.ReactNode {
  if (value === undefined || value === null || value === '') return '-';
  return String(value);
}

// New columns for grouped CP rules with expandable rows
export const cloudPropagationRuleColumns: GenericTableColumn<CloudPropagationRuleRow>[] = [
  { header: 'Sub ID', accessor: 'subId', renderCell: (row) => orDash(row.subId) },
  { header: 'Name', accessor: 'name', renderCell: (row) => orDash(row.name) },
  {
    header: 'BGP Neighbors',
    accessor: 'bgpNeighborsCount',
    renderCell: (row) => {
      const neighborTags =
        row.bgpNeighbors?.map((n) => `${n.neighborIp} (AS ${n.peerAs})`) ?? [];
      return (
        <TagsWithPopover
          tags={neighborTags}
          maxVisible={2}
          tagVariant="default"
        />
      );
    },
  },
  {
    header: 'MODE',
    accessor: 'mode',
    renderCell: (row) => (
      <GenericTag
        label={row.mode}
        variant={row.mode === 'Enterprise' ? 'neutral' : 'warning'}
      />
    ),
  },
  { header: 'Last Updated', accessor: 'lastUpdate', renderCell: (row) => orDash(row.lastUpdate) },
  {
    header: 'ACTIONS',
    accessor: undefined,
    renderCell: (row, rowIndex, onConfigure) => (
      <GenericButton
        isIconOnly
        icon={<IconConfig className="w-4 h-4 text-semantic-text-secondary" />}
        variant="secondary"
        shape="filled"
        size="sm"
        aria-label="Configure"
        onClick={() => onConfigure?.(rowIndex)}
      />
    ),
  },
];

/**
 * Formats a propagation rule ID as "CP-XXXXX" (padded to 5 digits)
 */
export function formatPropagationRuleSubId(ruleId: number): string {
  return `CP-${String(ruleId).padStart(5, '0')}`;
}

/**
 * Formats a propagation rule UUID as "CP-<first 8 chars>" for display
 */
export function formatPropagationRuleSubIdFromUuid(uuid: string): string {
  if (!uuid || typeof uuid !== 'string') return 'CP-?????';
  return `CP-${uuid.replace(/-/g, '').slice(0, 8).toUpperCase()}`;
}

/**
 * Formats a date for the Last Updated column: "4/28/2025, 2:30 PM"
 */
export function formatLastUpdate(date: Date | string | number): string {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Instance propagation rules list API response item (propagationRuleId as UUID, associatedNeighbors, inbounds, outbounds)
 */
export interface InstancePropagationRuleItem {
  propagationRuleId: string;
  name?: string;
  mode: 'TELCO' | 'ENTERPRISE';
  createdAt: string;
  updatedAt: string;
  associatedNeighbors: Array<{
    id: number;
    subUuid?: string;
    name?: string | null;
    neighborAddress: string;
    peerAs: number;
    localAs?: number;
    description?: string | null;
    operatingMode?: string;
    propagationMode?: string;
  }>;
  inbounds?: unknown[];
  outbounds?: unknown[];
}

/**
 * Transforms instance propagation rules list response (data[]) into CloudPropagationRuleRow[]
 */
export function transformInstancePropagationRulesToRuleRows(
  items: InstancePropagationRuleItem[]
): CloudPropagationRuleRow[] {
  return items.map((item) => {
    const normalizedMode = item.mode === 'TELCO' ? 'Telco' : 'Enterprise';
    const bgpNeighbors: BgpNeighborAssociation[] = (item.associatedNeighbors || []).map((n) => ({
      neighborIp: n.neighborAddress,
      peerAs: n.peerAs,
      bgpNeighborId: n.id,
    }));
    const lastUpdate = formatLastUpdate(item.updatedAt || item.createdAt || 0);
    return {
      subId: formatPropagationRuleSubIdFromUuid(item.propagationRuleId),
      name: item.name ?? undefined,
      bgpNeighborsCount: bgpNeighbors.length,
      bgpNeighbors,
      mode: normalizedMode,
      lastUpdate,
      propagationRuleId: item.propagationRuleId,
      reflectorInstanceId: undefined,
      ruleData: item as unknown as CloudPropagationRule,
    };
  });
}

/**
 * Interface for the new grouped API response structure
 */
export interface GroupedPropagationRuleApiItem {
  neighborAddress: string;
  bgpNeighbor: {
    id: number;
    neighborAddress: string;
    peerAs: number;
    operatingMode: string;
    updatedAt: string;
    reflectorInstanceId: number;
    [key: string]: any;
  };
  bgpNeighborIds: number[];
  inboundRule?: {
    id?: number;
    propagationRuleId: number;
    mode: string;
    configured: boolean;
    vrfs?: Array<{
      id: number;
      propagationRuleId: number;
      vrfName: string;
      routeTargets: string;
      [key: string]: any;
    }>;
    [key: string]: any;
  };
  outboundRule?: {
    id?: number;
    propagationRuleId: number;
    mode: string;
    configured: boolean;
    [key: string]: any;
  };
  hasInbound: boolean;
  hasOutbound: boolean;
  isFullyConfigured: boolean;
}

/**
 * Transforms the new API response format (with associatedNeighbors) into CloudPropagationRuleRow[]
 */
export function transformNewApiResponseToRuleRows(
  apiItems: NewPropagationRuleApiItem[]
): CloudPropagationRuleRow[] {
  return apiItems.map((item) => {
    // Normalize mode
    const normalizedMode = item.mode === 'TELCO' ? 'Telco' : 'Enterprise';
    
    // Map associatedNeighbors to BgpNeighborAssociation[]
    const bgpNeighbors: BgpNeighborAssociation[] = item.associatedNeighbors.map((neighbor) => ({
      neighborIp: neighbor.neighborAddress,
      peerAs: neighbor.peerAs,
      bgpNeighborId: neighbor.id, // Use id if available, otherwise undefined
    }));

    return {
      subId: item.propagationRuleSubUuid || formatPropagationRuleSubId(item.propagationRuleId),
      name: item.name ?? undefined,
      bgpNeighborsCount: bgpNeighbors.length,
      bgpNeighbors,
      mode: normalizedMode,
      lastUpdate: formatLastUpdate(item.lastUpdated),
      propagationRuleId: item.propagationRuleId,
      reflectorInstanceId: undefined, // Not available in new format
      ruleData: item as any, // Store full API item for reference
    };
  });
}

/**
 * Transforms the new grouped API response into CloudPropagationRuleRow[]
 * Groups by propagation rule ID (from inboundRule or outboundRule) and aggregates BGP neighbors
 */
export function transformGroupedApiResponseToRuleRows(
  apiItems: GroupedPropagationRuleApiItem[]
): CloudPropagationRuleRow[] {
  // Group by propagation rule ID
  // A propagation rule can have multiple BGP neighbors associated with it
  const ruleMap = new Map<number, {
    propagationRuleId: number;
    mode: 'Enterprise' | 'Telco';
    bgpNeighbors: BgpNeighborAssociation[];
    latestUpdate: string;
    name?: string;
    ruleData: any; // Store the first item's data for reference
  }>();

  apiItems.forEach((item) => {
    // Get propagation rule ID from inboundRule or outboundRule (they should have the same ID if both exist)
    const propagationRuleId = item.inboundRule?.propagationRuleId || item.outboundRule?.propagationRuleId;
    
    if (!propagationRuleId) {
      console.warn('Item missing propagationRuleId, skipping:', item);
      return;
    }

    // Determine mode from inboundRule or outboundRule (prefer inboundRule if available)
    const mode = (item.inboundRule?.mode || item.outboundRule?.mode || 'ENTERPRISE').toUpperCase();
    const normalizedMode = mode === 'TELCO' ? 'Telco' : 'Enterprise';

    // Get update time from BGP neighbor
    const itemUpdate = item.bgpNeighbor?.updatedAt || new Date().toISOString();

    const itemName = (item as any).name ?? item.inboundRule?.name ?? item.outboundRule?.name;
    if (!ruleMap.has(propagationRuleId)) {
      ruleMap.set(propagationRuleId, {
        propagationRuleId,
        mode: normalizedMode,
        bgpNeighbors: [],
        latestUpdate: itemUpdate,
        name: itemName,
        ruleData: item, // Store first item as reference
      });
    } else {
      const entry = ruleMap.get(propagationRuleId);
      if (entry && itemName != null && entry.name == null) entry.name = itemName;
    }

    const entry = ruleMap.get(propagationRuleId);
    if (!entry) return;

    // Update latest update time if this item is more recent
    if (itemUpdate > entry.latestUpdate) {
      entry.latestUpdate = itemUpdate;
    }

    // Add BGP neighbor association (avoid duplicates based on bgpNeighborId)
    if (item.bgpNeighbor) {
      const existingNeighbor = entry.bgpNeighbors.find(
        (n) => n.bgpNeighborId === item.bgpNeighbor.id
      );

      if (!existingNeighbor) {
        entry.bgpNeighbors.push({
          neighborIp: item.neighborAddress || item.bgpNeighbor.neighborAddress,
          peerAs: item.bgpNeighbor.peerAs,
          bgpNeighborId: item.bgpNeighbor.id,
        });
      }
    }
  });

  // Convert map to array of CloudPropagationRuleRow
  return Array.from(ruleMap.values()).map(({ propagationRuleId, mode, bgpNeighbors, latestUpdate, name, ruleData }) => {
    const formattedDate = formatLastUpdate(latestUpdate);

    return {
      subId: formatPropagationRuleSubId(propagationRuleId),
      name: name ?? undefined,
      bgpNeighborsCount: bgpNeighbors.length,
      bgpNeighbors,
      mode,
      lastUpdate: formattedDate,
      propagationRuleId,
      reflectorInstanceId: ruleData?.bgpNeighbor?.reflectorInstanceId,
      ruleData: ruleData, // Store full API item for reference
    };
  });
}

/**
 * Transforms CloudPropagationRule[] into grouped CloudPropagationRuleRow[]
 * Groups rules by propagation rule ID and aggregates BGP neighbors
 * 
 * Note: If multiple rules share the same propagation rule ID (rule.id), they will be grouped together.
 * Each rule's BGP neighbor will be added to the group's BGP neighbors array.
 */
export function transformToGroupedRuleRows(
  rules: CloudPropagationRule[]
): CloudPropagationRuleRow[] {
  // Group rules by propagation rule ID
  const ruleMap = new Map<number, {
    rule: CloudPropagationRule;
    bgpNeighbors: BgpNeighborAssociation[];
    latestUpdate: string; // Track the most recent update time
  }>();

  rules.forEach((rule) => {
    const ruleId = rule.id;
    const ruleUpdate = rule.updatedAt || rule.createdAt || new Date().toISOString();
    
    if (!ruleMap.has(ruleId)) {
      ruleMap.set(ruleId, {
        rule,
        bgpNeighbors: [],
        latestUpdate: ruleUpdate,
      });
    }

    const entry = ruleMap.get(ruleId);
    if (!entry) return; // Skip if entry doesn't exist (shouldn't happen, but TypeScript safety)
    
    // Update latest update time if this rule is more recent
    if (ruleUpdate > entry.latestUpdate) {
      entry.latestUpdate = ruleUpdate;
      entry.rule = rule; // Use the most recent rule as the primary rule data
    }
    
    // Add BGP neighbor association (avoid duplicates based on bgpNeighborId)
    if (rule.bgpNeighbor) {
      const existingNeighbor = entry.bgpNeighbors.find(
        (n) => n.bgpNeighborId === rule.bgpNeighbor.id
      );
      
      if (!existingNeighbor) {
        entry.bgpNeighbors.push({
          neighborIp: rule.bgpNeighbor.neighborAddress,
          peerAs: rule.bgpNeighbor.peerAs,
          bgpNeighborId: rule.bgpNeighbor.id,
        });
      }
    }
  });

  // Convert map to array of CloudPropagationRuleRow
  return Array.from(ruleMap.values()).map(({ rule, bgpNeighbors, latestUpdate }) => {
    const formattedDate = formatLastUpdate(latestUpdate);

    return {
      subId: formatPropagationRuleSubId(rule.id),
      name: rule.name ?? undefined,
      bgpNeighborsCount: bgpNeighbors.length,
      bgpNeighbors,
      mode: (rule.mode === 'Telco' ? 'Telco' : 'Enterprise') as 'Enterprise' | 'Telco',
      lastUpdate: formattedDate,
      propagationRuleId: rule.id,
      reflectorInstanceId: rule.bgpNeighbor?.reflectorInstanceId,
      ruleData: rule,
    };
  });
}


